import React, { useRef } from 'react';
import { Box, Fade, Popover } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';

import type { Diagnosis } from './PatientDiagnosesCard';
import { PatientDiagnosesCard } from './PatientDiagnosesCard';

interface PatientDiagnosesPopoverProps {
  diagnoses: Diagnosis[] | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isLoading?: boolean;
  onEdit?: () => void;
}

export const PatientDiagnosesPopover: React.FC<PatientDiagnosesPopoverProps> = ({
  diagnoses,
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
  isLoading = false,
  onEdit,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  const handlePopoverEnter = () => {
    if (onMouseEnter) {
      onMouseEnter();
    }
  };

  const handlePopoverLeave = () => {
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  if (!diagnoses && !isLoading) {
    return null;
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transitionDuration={200}
      sx={{
        pointerEvents: 'none',
        '& .MuiPopover-paper': {
          pointerEvents: 'auto',
          maxWidth: '90vw',
          mt: 0.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'borderColors.primary',
          boxShadow: 3,
        },
      }} slots={{ transition: Fade }}
    >
      <Box
        ref={popoverRef}
        onMouseEnter={handlePopoverEnter}
        onMouseLeave={handlePopoverLeave}
        sx={{ p: 0 }}
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <PatientDiagnosesCard diagnoses={diagnoses || []} isLoading={isLoading} onEdit={onEdit} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Popover>
  );
};

